import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { accountDeactivatedMessage, isAccountDeactivatedError } from '../../utils/accountStatus';
import { sanitizeEmail } from '../../utils/sanitize';
import { useI18n } from '../../i18n';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import './LoginPage.css';

export default function LoginPage() {
  const { t, language } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isResetMode, setIsResetMode] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError(t('Please enter email and password'));
      return;
    }
    setIsLoading(true);
    setError('');
    setSuccess('');
    try {
      await authService.signIn(sanitizeEmail(email), password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(isAccountDeactivatedError(err)
        ? accountDeactivatedMessage()
        : (err as Error).message || t('Login failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = sanitizeEmail(email);
    if (!cleanEmail) {
      setError(t('Please enter your email address'));
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      await authService.requestPasswordReset(cleanEmail, `${window.location.origin}/reset-password`, language);
      setSuccess(t('If an account exists for that email, a reset link has been sent.'));
    } catch {
      setError(t('Failed to send password reset email'));
    } finally {
      setIsLoading(false);
    }
  };

  const showResetForm = () => {
    setIsResetMode(true);
    setError('');
    setSuccess('');
  };

  const showLoginForm = () => {
    setIsResetMode(false);
    setError('');
    setSuccess('');
  };

  return (
    <div className="auth-layout">
      <div className="auth-layout__lang">
        <LanguageSwitcher />
      </div>
      <div className="auth-shell">
        <section className="auth-aside">
          <div className="auth-aside__eyebrow">{t('OdinsEye workspace')}</div>
          <h1 className="auth-aside__title">{t('Security operations without the spreadsheet feel.')}</h1>
          <p className="auth-aside__subtitle">
            {t('Give schedulers, supervisors and response teams a single place to manage coverage, alarms and field activity.')}
          </p>
          <div className="auth-aside__grid">
            <div className="auth-metric">
              <span className="auth-metric__label">{t('Coverage')}</span>
              <strong className="auth-metric__value">{t('Live staffing visibility across sites and shifts')}</strong>
            </div>
            <div className="auth-metric">
              <span className="auth-metric__label">{t('Response')}</span>
              <strong className="auth-metric__value">{t('Incident queues and panic alarms in one workflow')}</strong>
            </div>
            <div className="auth-metric">
              <span className="auth-metric__label">{t('Operations')}</span>
              <strong className="auth-metric__value">{t('Team, site and patrol coordination without context switching')}</strong>
            </div>
          </div>
        </section>

        <form onSubmit={isResetMode ? handlePasswordReset : handleSubmit} className="auth-card">
          <div className="auth-card__logo">OdinsEye</div>
          <div className="auth-card__title">{isResetMode ? t('Reset password') : t('Welcome back')}</div>
          <div className="auth-card__subtitle">
            {isResetMode
              ? t('Enter your email and we will send you a secure reset link.')
              : t('Sign in to manage operations or access an invited client portal.')}
          </div>
          {error && <div className="msg--error">{error}</div>}
          {success && <div className="msg--success">{success}</div>}
          <div className="login__form-group">
            <label className="form__label" htmlFor="email">{t('Email')}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form__input"
              placeholder={t('name@company.com')}
              autoComplete="email"
            />
          </div>
          {!isResetMode && (
            <div className="login__form-group">
              <div className="login__label-row">
                <label className="form__label" htmlFor="password">{t('Password')}</label>
                <button type="button" className="login__link-button" onClick={showResetForm}>
                  {t('Forgot password?')}
                </button>
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form__input"
                placeholder={t('Enter your password')}
                autoComplete="current-password"
              />
            </div>
          )}
          <button
            type="submit"
            disabled={isLoading}
            className="btn btn--primary btn--full-width login__submit"
          >
            {isLoading
              ? (isResetMode ? t('Sending reset link...') : t('Signing in...'))
              : (isResetMode ? t('Send reset link') : t('Sign in'))}
          </button>
          {isResetMode && (
            <button type="button" className="login__link-button login__link-button--center" onClick={showLoginForm}>
              {t('Back to sign in')}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
